import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { BookOpen, Check, Ear, Languages, Plus, RefreshCw, Volume2, X } from "lucide-react";
import { Button, Card, EmptyState, PageHeader } from "../components/ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import type { EnglishArticle, EnglishCard } from "../types";

function speak(text: string) {
  try {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 0.9;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  } catch {
    /* ignore */
  }
}

const CATEGORY_ZH: Record<string, string> = {
  factory: "工厂介绍",
  workflow: "业务流程",
  fabric: "面料知识",
  trade: "贸易条款",
  quality: "质检与标准"
};

export function EnglishPage() {
  const { data, dataRevision, mutate } = useApp();
  const [tab, setTab] = useState<"cards" | "articles">("cards");
  const [cards, setCards] = useState<EnglishCard[]>([]);
  const [articles, setArticles] = useState<EnglishArticle[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [articlesLoaded, setArticlesLoaded] = useState(false);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [todayDone, setTodayDone] = useState(0);
  const [openArticle, setOpenArticle] = useState<string | null>(null);
  const [showZh, setShowZh] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [articleError, setArticleError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const cardSequence = useRef(0);
  const articleSequence = useRef(0);

  const refresh = useCallback(async () => {
    const sequence = ++cardSequence.current;
    try {
      const result = await api.listEnglishCards();
      if (sequence !== cardSequence.current) return;
      setCards(result);
      setLoaded(true);
      setCardError(null);
    } catch (error) {
      if (sequence === cardSequence.current) setCardError(error instanceof Error ? error.message : "句子读取失败");
    }
  }, []);

  const refreshArticles = useCallback(async () => {
    const sequence = ++articleSequence.current;
    try {
      const result = await api.listEnglishArticles();
      if (sequence !== articleSequence.current) return;
      setArticles(result);
      setArticlesLoaded(true);
      setArticleError(null);
    } catch (error) {
      if (sequence === articleSequence.current) setArticleError(error instanceof Error ? error.message : "文章读取失败");
    }
  }, []);
  useEffect(() => {
    void refresh();
    void refreshArticles();
    return () => { cardSequence.current += 1; articleSequence.current += 1; };
  }, [dataRevision, refresh, refreshArticles]);

  const save = async <T,>(action: () => Promise<T>, message: string) => {
    if (pending.current) return null;
    pending.current = true;
    setSaving(true);
    try { return await mutate(action, message); }
    finally { pending.current = false; setSaving(false); }
  };

  let today: string;
  try { today = new Intl.DateTimeFormat("en-CA", { timeZone: data?.profile?.timezone || "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()); }
  catch { today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()); }

  const dailyDeck = cards.filter((card) => {
    if (card.learned === 0) return true;
    if (card.review_at && card.review_at <= today) return true;
    return false;
  });

  const currentIndex = Math.min(index, Math.max(0, dailyDeck.length - 1));
  const current = dailyDeck[currentIndex];

  const answer = async (correct: boolean) => {
    if (!current) return;
    const result = await save(() => api.markCardLearned(current.id, correct), correct ? "记对了" : "已记下，之后会按复习安排再练习");
    if (result === null) return;
    setRevealed(false);
    setTodayDone((done) => done + 1);
    setIndex(0);
    await refresh();
  };

  const submitCard = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const result = await save(
      () =>
        api.addEnglishCard({
          sentenceEn: String(form.get("sentenceEn") ?? ""),
          sentenceZh: String(form.get("sentenceZh") ?? ""),
          context: String(form.get("context") ?? ""),
          source: "custom"
        }),
      "卡片已加入词库"
    );
    if (result === null) return;
    setAdding(false);
    await refresh();
  };

  const learnedCount = cards.filter((card) => card.learned > 0).length;

  return (
    <div className="page page--english english-page">
      <PageHeader
        eyebrow="ENGLISH · 行业英语"
        title="英语学习"
        description="语言是通往更大世界的第二双眼睛。"
        actions={<Button variant="secondary" icon={<Plus size={15} />} onClick={() => setAdding(true)}>加句子</Button>}
      />

      {(tab === "cards" ? cardError : articleError) && <div role="alert"><p>{tab === "cards" ? "句子卡片" : "行业文章"}未能刷新：{tab === "cards" ? cardError : articleError}。已有内容仍保留。</p><Button variant="secondary" onClick={() => void (tab === "cards" ? refresh() : refreshArticles())}>重新读取</Button></div>}
      {!(tab === "cards" ? loaded : articlesLoaded) && !(tab === "cards" ? cardError : articleError) && <p role="status">正在读取{tab === "cards" ? "句子卡片" : "行业文章"}…</p>}

      <div className="english-tabs">
        <button className={tab === "cards" ? "active" : ""} onClick={() => setTab("cards")}>
          <Ear size={15} /> 句子卡片
        </button>
        <button className={tab === "articles" ? "active" : ""} onClick={() => setTab("articles")}>
          <BookOpen size={15} /> 行业文章
        </button>
      </div>

      {tab === "cards" ? (
        <>
          <div className="compound-strip">
            <div className="compound-chip"><Ear size={15} /><span>本次已学 <b>{todayDone}</b> 句</span></div>
            <div className="compound-chip"><Languages size={15} /><span>词库 <b>{cards.length}</b> 句 · 已学 <b>{learnedCount}</b> 句</span></div>
          </div>

          {adding && (
            <Card className="english-add">
              <h3><Plus size={16} /> 添加英语句子</h3>
              <form onSubmit={(event) => void submitCard(event)} className="plan-form">
                <div className="field field--full"><span>英文句子 *</span><input name="sentenceEn" required placeholder="如：Please send the tech pack first" /></div>
                <div className="field"><span>中文意思</span><input name="sentenceZh" placeholder="如：请先发工艺单" /></div>
                <div className="field"><span>来源语境</span><input name="context" placeholder="如：Sat 打样沟通 8/22" /></div>
                <div className="modal__actions"><Button disabled={saving} type="button" variant="ghost" onClick={() => setAdding(false)}>取消</Button><Button disabled={saving} type="submit">{saving ? "正在保存…" : "加入词库"}</Button></div>
              </form>
            </Card>
          )}

          {!current ? loaded && !cardError ? (
            <EmptyState icon={<Ear size={24} />} title={cards.length ? "当前没有到期的句子" : "词库还是空的"} description={cards.length ? "已学句子会按复习安排重新出现，也可以随时添加新的句子。" : "点右上角「加句子」，添加想练习的真实句子。"} />
          ) : null : (
            <div className="english-card-wrap">
              <div className="english-card">
                <div className="english-card__meta"><span>第 {currentIndex + 1} / {dailyDeck.length} 句</span><span>{current.source === "custom" ? "你自己加的" : current.context || "内置例句"}</span></div>
                <button className="english-card__listen" onClick={() => speak(current.sentence_en)} title="朗读"><Volume2 size={22} /></button>
                <h2>{current.sentence_en}</h2>
                {revealed ? (
                  <div className="english-card__answer"><p>{current.sentence_zh || "（还没有翻译，点「加句子」时补上）"}</p><small>{current.context}</small></div>
                ) : (
                  <button className="english-card__reveal" onClick={() => setRevealed(true)}>点这里看中文意思</button>
                )}
              </div>
              <div className="english-card__actions">
                <Button variant="secondary" icon={<RefreshCw size={15} />} onClick={() => speak(current.sentence_en)}>再听一遍</Button>
                <Button disabled={saving} variant="secondary" icon={<X size={15} />} onClick={() => void answer(false)}>不会</Button>
                <Button disabled={saving} icon={<Check size={15} />} onClick={() => void answer(true)}>{saving ? "正在保存…" : "记住了"}</Button>
              </div>
              <p className="english-hint">小技巧：先听 2 遍猜意思，再看翻译，最后听 1 遍跟读。每天 5 句就够。</p>
            </div>
          )}
        </>
      ) : (
        <div className="article-list">
          {articles.length === 0 && articlesLoaded && !articleError ? (
            <EmptyState icon={<BookOpen size={24} />} title="还没有文章" description="这里展示已添加的行业文章。" />
          ) : (
            articles.map((article) => (
              <Card key={article.id} className="article-card">
                <div
                  className="article-card__head"
                  onClick={() => {
                    setOpenArticle(openArticle === article.id ? null : article.id);
                    setShowZh(false);
                  }}
                >
                  <h3>{article.title_en}</h3>
                  <p>{article.title_zh} · {CATEGORY_ZH[article.category] ?? article.category}</p>
                </div>
                {openArticle === article.id && (
                  <div className="article-body">
                    <div className="article-actions">
                      <Button variant="secondary" icon={<Volume2 size={15} />} onClick={() => speak(article.content_en)}>朗读全文</Button>
                      <Button variant="secondary" icon={<Languages size={15} />} onClick={() => setShowZh(!showZh)}>{showZh ? "隐藏中文" : "显示中文"}</Button>
                    </div>
                    {article.content_en.split("\n").filter(Boolean).map((para, i) => (
                      <p key={i} className="article-para">
                        <button className="article-para__say" onClick={() => speak(para)} title="朗读本段"><Volume2 size={13} /></button>
                        {para}
                      </p>
                    ))}
                    {showZh &&
                      article.content_zh.split("\n").filter(Boolean).map((para, i) => (
                        <p key={`zh-${i}`} className="article-para article-para--zh">{para}</p>
                      ))}
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
